import { ArrowLeft, FileCode2, FlaskConical, FolderOpen, GitCommitHorizontal, Loader2, RefreshCw } from "lucide-react"
import { observer } from "mobx-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { DIFF_LAB_STRATEGIES, DiffStrategyView, type DiffLabStrategy } from "../components/diffLab/DiffStrategyView"
import { selectDirectory } from "../electronAPIs/shell"
import {
    type ChangedFileInfo,
    type GetDifftasticResponse,
    type GetFilePairResponse,
    type GetFilePatchResponse,
    type GitLogEntry,
    type GitSummaryResponse,
    type GitTarget,
    gitApi,
} from "../nestAPIs/git"
import { getPlatformAdapter } from "../platform/adapter"
import { useCodeNavigate } from "../routing"
import { inspectTaskDirectoryPath } from "../store/taskDirectory"
import type { Repo } from "../types"

const PAGE_SIZE = 100
const EMPTY_TREE = "4b825dc642cb6eb9a060e54bf8d69288fbee4904"

interface DiffLabPageProps {
    workspaceId: string
    repo: Repo
}

interface ActiveRepository {
    path: string
    target: GitTarget
    branch: string | null
}

function normalizePath(path: string): string {
    const normalized = path.replace(/\\/g, "/").replace(/\/+$/g, "")
    return /^[A-Za-z]:\//.test(normalized) ? normalized.toLowerCase() : normalized
}

function changedWorkingFiles(summary: GitSummaryResponse | null): ChangedFileInfo[] {
    if (!summary) return []
    const files = new Map<string, ChangedFileInfo>()
    for (const [changeSet, fallbackStatus] of [
        [summary.staged, "modified"],
        [summary.unstaged, "modified"],
        [summary.untracked, "added"],
    ] as const) {
        for (const file of changeSet.files) {
            const previous = files.get(file.path)
            files.set(file.path, {
                path: file.path,
                status: file.status ?? fallbackStatus,
                binary: previous?.binary === true || file.binary,
            })
        }
    }
    return [...files.values()].sort((left, right) => left.path.localeCompare(right.path))
}

function historyLabel(commit: GitLogEntry): string {
    return `${commit.shortSha} ${commit.message}`
}

export const DiffLabPage = observer(function DiffLabPage({ workspaceId, repo }: DiffLabPageProps) {
    const navigate = useCodeNavigate()
    const canBrowse = getPlatformAdapter().capabilities.hostDirectoryPicker === "available"
    const [pathInput, setPathInput] = useState(repo.path)
    const [activeRepository, setActiveRepository] = useState<ActiveRepository | null>(null)
    const [repositoryLoading, setRepositoryLoading] = useState(false)
    const [repositoryError, setRepositoryError] = useState<string | null>(null)
    const [refreshToken, setRefreshToken] = useState(0)
    const [branches, setBranches] = useState<string[]>([])
    const [selectedRef, setSelectedRef] = useState("HEAD")
    const [commits, setCommits] = useState<GitLogEntry[]>([])
    const [historyLoading, setHistoryLoading] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [summary, setSummary] = useState<GitSummaryResponse | null>(null)
    const [selectedHistory, setSelectedHistory] = useState<string>("working")
    const [files, setFiles] = useState<ChangedFileInfo[]>([])
    const [filesLoading, setFilesLoading] = useState(false)
    const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null)
    const [strategy, setStrategy] = useState<DiffLabStrategy>("current")
    const [diffStyle, setDiffStyle] = useState<"split" | "unified">("split")
    const [pair, setPair] = useState<GetFilePairResponse | null>(null)
    const [patch, setPatch] = useState<GetFilePatchResponse | null>(null)
    const [diffLoading, setDiffLoading] = useState(false)
    const [diffError, setDiffError] = useState<string | null>(null)
    const [pairElapsedMs, setPairElapsedMs] = useState<number | null>(null)
    const [patchElapsedMs, setPatchElapsedMs] = useState<number | null>(null)
    const [difftastic, setDifftastic] = useState<GetDifftasticResponse | null>(null)
    const [difftasticLoading, setDifftasticLoading] = useState(false)
    const [difftasticRoundTripMs, setDifftasticRoundTripMs] = useState<number | null>(null)

    const workingFiles = useMemo(() => changedWorkingFiles(summary), [summary])
    const selectedCommit = useMemo(() => commits.find((commit) => commit.sha === selectedHistory) ?? null, [commits, selectedHistory])
    const selectedFile = useMemo(() => files.find((file) => file.path === selectedFilePath) ?? files[0] ?? null, [files, selectedFilePath])

    const activateRepository = useCallback(
        async (requestedPath: string) => {
            setRepositoryLoading(true)
            setRepositoryError(null)
            try {
                const inspection = await inspectTaskDirectoryPath(requestedPath, workspaceId)
                if (!inspection.ok) {
                    setRepositoryError(inspection.error)
                    return
                }
                const root = inspection.path
                const external = normalizePath(root) !== normalizePath(repo.path)
                const target: GitTarget = {
                    workspaceId,
                    workspaceRoot: external ? root : repo.path,
                    directory: root,
                    ...(external ? { worktreeId: root } : {}),
                }
                const status = await gitApi.getGitSummary(target)
                setPathInput(root)
                setActiveRepository({ path: root, target, branch: status.branch })
                setSelectedRef("HEAD")
                setSelectedHistory("working")
                setSelectedFilePath(null)
                setSummary(status)
                setRefreshToken((value) => value + 1)
            } catch (cause) {
                setRepositoryError(cause instanceof Error ? cause.message : "The repository could not be loaded.")
            } finally {
                setRepositoryLoading(false)
            }
        },
        [repo.path, workspaceId]
    )

    useEffect(() => {
        void activateRepository(repo.path)
    }, [activateRepository, repo.path])

    useEffect(() => {
        const active = activeRepository
        if (!active) return
        let cancelled = false
        setHistoryLoading(true)
        setRepositoryError(null)
        void Promise.all([
            gitApi.listBranches({ ...active.target, includeRemote: true }),
            gitApi.getLog({ ...active.target, ref: selectedRef, limit: PAGE_SIZE, skip: 0 }),
            gitApi.getGitSummary(active.target),
        ])
            .then(([branchResult, logResult, status]) => {
                if (cancelled) return
                setBranches([...new Set(["HEAD", ...branchResult.branches.map((branch) => branch.name)])])
                setCommits(logResult.commits)
                setHasMore(logResult.hasMore)
                setSummary(status)
                setActiveRepository((current) => (current ? { ...current, branch: status.branch } : current))
                setSelectedHistory((current) => {
                    if (current === "working" && changedWorkingFiles(status).length > 0) return current
                    if (logResult.commits.some((commit) => commit.sha === current)) return current
                    return changedWorkingFiles(status).length > 0 ? "working" : (logResult.commits[0]?.sha ?? "working")
                })
            })
            .catch((cause: unknown) => {
                if (!cancelled) setRepositoryError(cause instanceof Error ? cause.message : "Git history could not be loaded.")
            })
            .finally(() => {
                if (!cancelled) setHistoryLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [activeRepository?.path, refreshToken, selectedRef])

    useEffect(() => {
        const active = activeRepository
        if (!active) return
        if (selectedHistory === "working") {
            setFiles(workingFiles)
            setFilesLoading(false)
            return
        }
        const commit = selectedCommit
        if (!commit) {
            setFiles([])
            return
        }
        let cancelled = false
        setFilesLoading(true)
        void gitApi
            .getCommitFiles({ ...active.target, commit: commit.sha })
            .then((result) => {
                if (!cancelled) setFiles(result.files)
            })
            .catch((cause: unknown) => {
                if (!cancelled) setDiffError(cause instanceof Error ? cause.message : "Changed files could not be loaded.")
            })
            .finally(() => {
                if (!cancelled) setFilesLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [activeRepository?.path, selectedCommit?.sha, selectedHistory, workingFiles])

    useEffect(() => {
        setSelectedFilePath((current) => {
            if (current && files.some((file) => file.path === current)) return current
            return files[0]?.path ?? null
        })
    }, [files])

    useEffect(() => {
        const active = activeRepository
        const file = selectedFile
        if (!active || !file || file.binary) {
            setPair(null)
            setPatch(null)
            setDiffLoading(false)
            return
        }
        let cancelled = false
        setDiffLoading(true)
        setDiffError(null)
        setPair(null)
        setPatch(null)
        setDifftastic(null)
        setPairElapsedMs(null)
        setPatchElapsedMs(null)
        setDifftasticRoundTripMs(null)

        const loadPair = async (): Promise<GetFilePairResponse> => {
            if (selectedHistory === "working") {
                return gitApi.getFilePair({ ...active.target, fromTreeish: "HEAD", toTreeish: "", filePath: file.path, oldPath: file.oldPath })
            }
            if (!selectedCommit) return { before: "", after: "" }
            if (selectedCommit.parentCount === 0) {
                const after = await gitApi.getFileAtTreeish({ ...active.target, treeish: selectedCommit.sha, filePath: file.path })
                return { before: "", after: after.content, tooLarge: after.tooLarge }
            }
            return gitApi.getFilePair({
                ...active.target,
                fromTreeish: `${selectedCommit.sha}^`,
                toTreeish: selectedCommit.sha,
                filePath: file.path,
                oldPath: file.oldPath,
            })
        }
        const loadPatch = (): Promise<GetFilePatchResponse> =>
            selectedHistory === "working"
                ? gitApi.getWorktreeFilePatch({ ...active.target, fromTreeish: "HEAD", filePath: file.path, oldPath: file.oldPath, contextLines: 25 })
                : gitApi.getCommitFilePatch({
                      ...active.target,
                      commit: selectedCommit?.sha ?? selectedHistory,
                      filePath: file.path,
                      oldPath: file.oldPath,
                      contextLines: 25,
                  })

        const pairStarted = performance.now()
        const pairRequest = loadPair().then((result) => ({ result, elapsedMs: performance.now() - pairStarted }))
        const patchStarted = performance.now()
        const patchRequest = loadPatch().then((result) => ({ result, elapsedMs: performance.now() - patchStarted }))
        void Promise.all([pairRequest, patchRequest])
            .then(([nextPair, nextPatch]) => {
                if (cancelled) return
                setPair(nextPair.result)
                setPatch(nextPatch.result)
                setPairElapsedMs(nextPair.elapsedMs)
                setPatchElapsedMs(nextPatch.elapsedMs)
            })
            .catch((cause: unknown) => {
                if (!cancelled) setDiffError(cause instanceof Error ? cause.message : "The comparison could not be loaded.")
            })
            .finally(() => {
                if (!cancelled) setDiffLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [activeRepository?.path, selectedCommit?.parentCount, selectedCommit?.sha, selectedFile?.path, selectedHistory])

    useEffect(() => {
        const active = activeRepository
        const file = selectedFile
        if (strategy !== "difftastic" || !active || !file || file.binary || diffLoading) return
        let cancelled = false
        setDifftasticLoading(true)
        setDifftastic(null)
        setDifftasticRoundTripMs(null)
        const fromTreeish =
            selectedHistory === "working" ? "HEAD" : selectedCommit?.parentCount === 0 ? EMPTY_TREE : `${selectedCommit?.sha ?? selectedHistory}^`
        const toTreeish = selectedHistory === "working" ? "" : (selectedCommit?.sha ?? selectedHistory)
        const started = performance.now()
        void gitApi
            .getDifftastic({ ...active.target, fromTreeish, toTreeish, filePath: file.path, oldPath: file.oldPath, contextLines: 3 })
            .then((result) => {
                if (!cancelled) {
                    setDifftastic(result)
                    setDifftasticRoundTripMs(performance.now() - started)
                }
            })
            .catch((cause: unknown) => {
                if (!cancelled) {
                    setDifftasticRoundTripMs(performance.now() - started)
                    setDifftastic({
                        available: true,
                        output: "",
                        error: cause instanceof Error ? cause.message : "Structural preview failed.",
                        elapsedMs: 0,
                        truncated: false,
                        hitTimeout: false,
                    })
                }
            })
            .finally(() => {
                if (!cancelled) setDifftasticLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [activeRepository?.path, diffLoading, selectedCommit?.parentCount, selectedCommit?.sha, selectedFile?.path, selectedHistory, strategy])

    const loadMore = async () => {
        if (!activeRepository || historyLoading || !hasMore) return
        setHistoryLoading(true)
        try {
            const result = await gitApi.getLog({ ...activeRepository.target, ref: selectedRef, limit: PAGE_SIZE, skip: commits.length })
            setCommits((current) => [...current, ...result.commits])
            setHasMore(result.hasMore)
        } finally {
            setHistoryLoading(false)
        }
    }

    const browse = async () => {
        const selected = await selectDirectory(pathInput)
        if (selected) {
            setPathInput(selected)
            await activateRepository(selected)
        }
    }

    return (
        <div className="flex h-full min-h-0 flex-col bg-base-100">
            <header className="shrink-0 border-b border-border bg-base-200 px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate.go("CodeWorkspaceTaskCreate", { workspaceId })}
                        className="btn flex h-9 w-9 items-center justify-center border border-border bg-base-100 text-muted hover:bg-base-300 hover:text-base-content"
                        aria-label="Back to project"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <FlaskConical size={16} className="text-primary" />
                            <h1 className="text-sm font-semibold text-base-content">Diff Lab</h1>
                            <span className="border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-warning">
                                Experimental
                            </span>
                        </div>
                        <p className="mt-0.5 text-xs text-muted">Compare the same real change through multiple diff engines. Current remains the baseline.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setRefreshToken((value) => value + 1)}
                        className="btn flex h-9 items-center gap-2 border border-border bg-base-100 px-3 text-xs text-muted hover:bg-base-300 hover:text-base-content"
                        disabled={!activeRepository || historyLoading}
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>
                <form
                    className="mt-3 flex flex-wrap items-center gap-2"
                    onSubmit={(event) => {
                        event.preventDefault()
                        void activateRepository(pathInput)
                    }}
                >
                    <input
                        value={pathInput}
                        onChange={(event) => setPathInput(event.target.value)}
                        className="h-9 min-w-64 flex-1 border border-border bg-input px-3 font-mono text-xs text-base-content outline-none focus:border-primary"
                        aria-label="Git directory"
                        placeholder="Select or enter a Git directory"
                    />
                    {canBrowse && (
                        <button
                            type="button"
                            onClick={() => void browse()}
                            className="btn flex h-9 items-center gap-2 border border-border bg-base-100 px-3 text-xs hover:bg-base-300"
                        >
                            <FolderOpen size={14} /> Browse
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={repositoryLoading || !pathInput.trim()}
                        className="btn flex h-9 items-center gap-2 bg-primary px-4 text-xs text-primary-content hover:opacity-90 disabled:opacity-50"
                    >
                        {repositoryLoading && <Loader2 size={14} className="animate-spin" />} Load repository
                    </button>
                    <select
                        value={selectedRef}
                        onChange={(event) => setSelectedRef(event.target.value)}
                        disabled={!activeRepository || branches.length === 0}
                        className="h-9 max-w-64 border border-border bg-base-100 px-2 text-xs text-base-content outline-none focus:border-primary"
                        aria-label="Git branch or ref"
                    >
                        {branches.map((branch) => (
                            <option key={branch} value={branch}>
                                {branch}
                            </option>
                        ))}
                    </select>
                </form>
                {repositoryError && <div className="mt-2 text-xs text-error">{repositoryError}</div>}
            </header>

            <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[15rem_17rem_minmax(0,1fr)]">
                <aside className="flex min-h-0 flex-col border-b border-border bg-base-200 md:border-r md:border-b-0">
                    <div className="shrink-0 border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted">History</div>
                    <div className="max-h-52 min-h-0 flex-1 overflow-y-auto md:max-h-none">
                        {workingFiles.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setSelectedHistory("working")}
                                className={`btn w-full border-b border-border/50 px-3 py-2 text-left ${selectedHistory === "working" ? "bg-primary/10" : "hover:bg-base-300"}`}
                            >
                                <div className="text-xs font-medium text-base-content">Working tree</div>
                                <div className="mt-0.5 text-[11px] text-muted">{workingFiles.length} changed file(s)</div>
                            </button>
                        )}
                        {commits.map((commit) => (
                            <button
                                key={commit.sha}
                                type="button"
                                onClick={() => setSelectedHistory(commit.sha)}
                                className={`btn w-full border-b border-border/50 px-3 py-2 text-left ${selectedHistory === commit.sha ? "bg-primary/10" : "hover:bg-base-300"}`}
                                title={historyLabel(commit)}
                            >
                                <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted">
                                    <GitCommitHorizontal size={12} /> {commit.shortSha}
                                </div>
                                <div className="mt-0.5 truncate text-xs text-base-content">{commit.message}</div>
                                <div className="mt-0.5 truncate text-[10px] text-muted">
                                    {commit.author} · {commit.relativeDate}
                                </div>
                            </button>
                        ))}
                        {historyLoading && commits.length === 0 && <div className="p-4 text-center text-xs text-muted">Loading history…</div>}
                    </div>
                    {hasMore && (
                        <button
                            type="button"
                            onClick={() => void loadMore()}
                            disabled={historyLoading}
                            className="btn h-9 shrink-0 border-t border-border text-xs text-muted hover:bg-base-300"
                        >
                            {historyLoading ? "Loading…" : "Load more"}
                        </button>
                    )}
                </aside>

                <aside className="flex min-h-0 flex-col border-b border-border bg-base-200 md:border-r md:border-b-0">
                    <div className="shrink-0 border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
                        Changed files · {files.length}
                    </div>
                    <div className="max-h-52 min-h-0 flex-1 overflow-y-auto md:max-h-none">
                        {filesLoading ? (
                            <div className="p-4 text-center text-xs text-muted">Loading files…</div>
                        ) : files.length === 0 ? (
                            <div className="p-4 text-center text-xs text-muted">No files in this change.</div>
                        ) : (
                            files.map((file) => (
                                <button
                                    key={file.path}
                                    type="button"
                                    onClick={() => setSelectedFilePath(file.path)}
                                    className={`btn flex w-full items-start gap-2 border-b border-border/50 px-3 py-2 text-left ${selectedFile?.path === file.path ? "bg-primary/10" : "hover:bg-base-300"}`}
                                    title={file.path}
                                >
                                    <FileCode2 size={14} className="mt-0.5 shrink-0 text-muted" />
                                    <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-base-content">{file.path}</span>
                                    <span className="text-[10px] uppercase text-muted">{file.status.slice(0, 1)}</span>
                                </button>
                            ))
                        )}
                    </div>
                </aside>

                <main className="flex min-h-0 min-w-0 flex-col">
                    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-base-200 px-3 py-2">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                            {DIFF_LAB_STRATEGIES.map((entry) => (
                                <button
                                    key={entry.id}
                                    type="button"
                                    onClick={() => setStrategy(entry.id)}
                                    className={`btn h-8 border px-2.5 text-xs ${strategy === entry.id ? "border-primary bg-primary/10 text-base-content" : "border-border bg-base-100 text-muted hover:bg-base-300 hover:text-base-content"}`}
                                    title={entry.description}
                                >
                                    {entry.label}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center border border-border bg-base-100">
                            {(["split", "unified"] as const).map((style) => (
                                <button
                                    key={style}
                                    type="button"
                                    onClick={() => setDiffStyle(style)}
                                    className={`btn h-8 px-2 text-xs capitalize ${diffStyle === style ? "bg-base-300 text-base-content" : "text-muted hover:text-base-content"}`}
                                >
                                    {style}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="shrink-0 border-b border-border bg-base-100 px-3 py-2">
                        <div className="truncate font-mono text-xs text-base-content">{selectedFile?.path ?? "Select a file"}</div>
                        <div className="mt-0.5 text-[11px] text-muted">{DIFF_LAB_STRATEGIES.find((entry) => entry.id === strategy)?.description}</div>
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto">
                        {selectedFile ? (
                            <DiffStrategyView
                                strategy={strategy}
                                file={selectedFile}
                                pair={pair}
                                patch={patch}
                                loading={diffLoading}
                                error={diffError}
                                diffStyle={diffStyle}
                                difftastic={difftastic}
                                difftasticLoading={difftasticLoading}
                                pairElapsedMs={pairElapsedMs}
                                patchElapsedMs={patchElapsedMs}
                                difftasticRoundTripMs={difftasticRoundTripMs}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center p-6 text-sm text-muted">
                                Choose a commit and file to compare strategies.
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    )
})
